import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface CostAlert {
  alert_kind: string;
  threshold: number;
  current_value: number;
  message: string;
}

interface CostAlertPopupProps {
  alerts: CostAlert[];
  onDismiss: (alertKind: string) => void;
}

export default function CostAlertPopup({ alerts, onDismiss }: CostAlertPopupProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleAddFunds = useCallback(() => {
    navigate('/dashboard/billing');
  }, [navigate]);

  // Hide on compute page — ComputePage has its own inline balance banner
  if (location.pathname === '/dashboard/compute') return null;
  if (alerts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-3 max-w-sm">
      {alerts.map((alert) => (
        <div
          key={alert.alert_kind}
          className="rounded-lg shadow-2xl border overflow-hidden backdrop-blur-sm bg-amber-950/90 border-amber-500/40"
        >
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-amber-500/20">
                <svg className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.736 6.979C9.208 6.193 9.696 6 10 6c.304 0 .792.193 1.264.979a1 1 0 001.715-1.029C12.279 4.784 11.232 4 10 4s-2.279.784-2.979 1.95c-.285.475-.507 1.007-.643 1.55h5.244a1 1 0 010 2H6.378c-.01.165-.014.331-.014.5s.005.335.014.5h5.244a1 1 0 010 2H6.378c.136.543.358 1.075.643 1.55C7.721 15.216 8.768 16 10 16s2.279-.784 2.979-1.95a1 1 0 011.715 1.029C13.979 16.193 12.232 17 10 17s-3.979-.807-4.694-1.921A6.486 6.486 0 014.364 12.5H3a1 1 0 010-2h1.07a8.27 8.27 0 010-1H3a1 1 0 110-2h1.364c.27-.957.697-1.864 1.294-2.579z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-amber-300">
                  {alert.alert_kind === 'daily_limit' ? 'Daily Spending Limit' : 'Cost Alert'}
                </h4>
                <p className="text-xs text-gray-300 mt-1">{alert.message}</p>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleAddFunds}
                className="flex-1 px-3 py-1.5 rounded text-xs font-medium transition-colors text-white bg-amber-600 hover:bg-amber-500"
              >
                Add Funds
              </button>
              <button
                onClick={() => onDismiss(alert.alert_kind)}
                className="flex-1 px-3 py-1.5 rounded text-xs font-medium transition-colors bg-gray-700 hover:bg-gray-600 text-gray-300"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
